import { CloseIcon, SearchIcon } from "@xola/icons";
import { Label, Modal, SubmitButton, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import {
    formatDate,
    FormField,
    handleSuccessfulSubmission,
    InputField,
    isEquipmentBased,
    isPurchasePhoneRequired,
    isValidTimeZoneId,
    requiredEmail,
    requiredNumber,
    requiredString,
} from "common";
import { isNil, reduce, sum } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    CatalogItem,
    EquipmentCatalogItem,
    Event,
    Experience,
    Permission,
    PurchaseItemObject,
    Seller,
    Source,
    TimeRange,
    Timeslot,
    UnitType,
    Waitlist,
} from "types";
import { boolean, number, object, ref, string } from "yup";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { useTimeslots } from "../../hooks/useTimeslots";
import { useVisibility } from "../../hooks/useVisibility";
import { getCatalogItemByTemplateId, getCatalogItemsByUnitType } from "../../models/Catalog";
import { getCreatePurchaseAddonLineItems, makeExperiencePurchaseItem } from "../../models/CreatePurchaseState";
import { isDefaultDemographic } from "../../models/DemographicUnit";
import { getDemographicLineItems, getEquipmentRosterLineItems } from "../../models/PurchaseItem";
import {
    AddonLineItem,
    CreatePurchaseItemLineItem,
    DemographicLineItem,
    useCreatePurchaseStore,
} from "../../stores/createPurchaseStore";
import { IsGranted } from "../Common/IsGranted";
import { NoTranslate } from "../Common/NoTranslate";
import { QuantitySpinner } from "../Common/QuantitySpinner/QuantitySpinner";
import { SecondaryButton } from "../Common/SecondaryButton";
import { TimeRangeSelector } from "../Common/TimeRangeSelector";
import { XolaFormik } from "../Common/XolaFormik";
import { Loader } from "../Loader";
import { SearchCustomerModal } from "../PurchaseCreate/SearchCustomerModal";
import { updateTimeslotCache } from "./EditEventCapacityModal";
import { EventSummaryTile } from "./EventSummaryTile";
import { JoinWaitlistAvailableSpots, JoinWaitlistPayload } from "./JoinWaitlistAvailableSpots";

type InitialLineItem = CreatePurchaseItemLineItem | AddonLineItem;

interface JoinWaitlistModalProps {
    isOpen: boolean;
    onClose: () => void;
    timeslot: Timeslot;
    onSuccess?: () => void;
    initialLineItems?: InitialLineItem[];
    onQuantityChange?: (catalog: CatalogItem, quantity: number) => void;
    timezone?: string;
}

export const JoinWaitlistModal = ({
    isOpen,
    onClose,
    timeslot,
    onSuccess,
    initialLineItems,
    onQuantityChange,
    timezone,
}: JoinWaitlistModalProps) => {
    const { data: experience, isLoading: experienceLoading } = useResource<Experience>([
        "experiences",
        timeslot.product,
    ]);
    const lineItems = experience ? makeExperiencePurchaseItem(experience)?.lineItems ?? [] : [];
    initialLineItems = initialLineItems ?? lineItems;
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            {experienceLoading ? (
                <Loader />
            ) : (
                <ModalContent
                    timeslot={timeslot}
                    initialLineItems={initialLineItems}
                    timezone={timezone}
                    experience={experience}
                    onClose={onClose}
                    onSuccess={onSuccess}
                    onQuantityChange={onQuantityChange}
                />
            )}
        </Modal>
    );
};

interface FormValues {
    name: string;
    email: string;
    phone: string;
    flexible: boolean;
    timeRange: Partial<TimeRange> & { flexible: boolean };
    totalGuests: number;
}

interface ModalContentProps {
    timeslot: Timeslot;
    onClose: () => void;
    onSuccess?: () => void;
    initialLineItems?: InitialLineItem[];
    onQuantityChange?: (catalog: CatalogItem, quantity: number) => void;
    timezone?: string;
    experience?: Experience;
}

const ModalContent = ({
    timeslot,
    onClose,
    onSuccess,
    onQuantityChange,
    initialLineItems = [],
    timezone,
    experience,
}: ModalContentProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const [guests, setGuests] = useState<GuestSpinnerState>({} as GuestSpinnerState);
    const [isCustomerSelected, setCustomerSelected] = useState(false);
    const [isCustomerSearchModal, { show: showCustomerSearchModal, hide: hideCustomerSearchModal }] =
        useVisibility(false);
    const setWaitlistId = useCreatePurchaseStore((state) => state.actions.setWaitlistId);

    const { data: event, isLoading: eventLoading } = useResource<Event>(["events", timeslot?.event?.id], null, {
        enabled: !!timeslot?.event?.id,
    });

    const totalQuantity = getTotalGuests(guests);

    const { data: timeslots } = useTimeslots({
        product: timeslot.product,
        quantity: totalQuantity,
        start: timeslot.arrivalDatetime,
        showAllTimeslots: false,
        isPastAvailabilityChecked: false,
        isSingleDay: true,
    });

    // Secondary modal logic
    const [joinWaitlistPayload, setJoinWaitlistPayload] = useState<JoinWaitlistPayload | null>(null);
    const [timeRange, setTimeRange] = useState<Partial<TimeRange> & { flexible: boolean }>();
    const [timeslotsWithinRange, setTimeslotsWithinRange] = useState<Timeslot[] | undefined>();

    const {
        mutateAsync: joinWaitlist,
        isLoading,
        isSuccess,
    } = useMutation(async (payload: any) => {
        const { data }: { data: Waitlist } = await api.post(`/waitlists`, payload);
        setWaitlistId(data.id);
        return data;
    });

    const initialQuantity = useMemo(
        () => [
            ...getDemographicLineItems({ lineItems: initialLineItems ?? [] }),
            ...getEquipmentRosterLineItems({ lineItems: initialLineItems ?? [] }),
        ],
        [initialLineItems],
    );

    useEffect(() => {
        if (experience && initialQuantity?.length > 0) {
            setGuests(getInitialSpinnerState(getWaitlistCatalogItems(experience), initialQuantity));
        }
    }, [experience, initialQuantity]);

    if (!experience || eventLoading) {
        return <Loader />;
    }

    const totalGuests = getTotalGuests(guests);

    const handleFormSubmit = async (values: FormValues) => {
        const { name, email, phone, timeRange } = values;

        const payload: JoinWaitlistPayload = {
            customerName: name,
            email,
            currency: seller.currency,
            phone: String(phone),
            seller: { id: seller.id },
            items: [getWaitlistLineItemPayload(timeslot, experience, initialLineItems, guests, timeRange, timezone)],
            lineItems: [],
            status: "waitlist",
        };

        if (timeRange.flexible) {
            const { startTime, endTime } = timeRange;
            const timeslotsWithinRange = timeslots?.filter((timeslot) => {
                const { time } = timeslot;
                return time && startTime && endTime && timeslot.canBook && time >= startTime && time <= endTime;
            });

            if (timeslotsWithinRange && timeslotsWithinRange.length > 0) {
                setJoinWaitlistPayload(payload);
                setTimeslotsWithinRange(timeslotsWithinRange);
                setTimeRange(timeRange);
            } else {
                await handleSubmit(payload);
            }
        } else {
            await handleSubmit(payload);
        }
    };

    const handleSubmit = async (payload?: JoinWaitlistPayload) => {
        await joinWaitlist(payload ?? joinWaitlistPayload);
        handleSuccessfulSubmission(() => {
            invalidate("purchaseItems");
            invalidate("events", event?.id);

            const timeslotParams = {
                timeslot: event ? null : timeslot,
                event: event ?? null,
                productId: null,
                startDate: null,
            };

            const updateParams = {
                isGranted: isGranted,
            };

            updateTimeslotCache(timeslotParams, updateParams);

            if (onSuccess) {
                onSuccess();
            } else {
                onClose();
            }
        });
    };

    const handleRemoveSelectedCustomer = (setFieldValue: (field: string, value: string) => void) => () => {
        setFieldValue("name", "");
        setFieldValue("email", "");
        setFieldValue("phone", "");

        setCustomerSelected(false);
    };

    const validationSchema = getValidationSchema(seller, isCustomerSelected);
    const initialValues = getInitialValues(getWaitlistCatalogItems(experience), initialQuantity ?? []);
    const isWaitlistLimited = experience?.waitlist?.limit?.enabled === true;
    const shouldShowFlexibleAvailability =
        timeslot.time !== null && !isWaitlistLimited && !isEquipmentBased(experience);

    if (timeslotsWithinRange && timeslotsWithinRange.length > 0 && joinWaitlistPayload && timeRange && experience) {
        return (
            <JoinWaitlistAvailableSpots
                timeRange={timeRange}
                timeslots={timeslotsWithinRange}
                quantity={totalQuantity}
                isLoading={isLoading}
                isSuccess={isSuccess}
                experience={experience}
                onSubmit={async () => handleSubmit()}
                onClose={onClose}
            />
        );
    }

    return (
        <XolaFormik
            validateOnBlur
            validateOnChange={false}
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
        >
            {({ handleSubmit, values, setFieldValue, errors, validateField }) => {
                return (
                    <>
                        <Modal.Header>Join Waitlist</Modal.Header>
                        <Modal.Body>
                            <div className="space-y-10">
                                {event ? (
                                    <div className="space-y-6">
                                        <EventSummaryTile
                                            event={event}
                                            className="mb-0"
                                            quantity={timeslot.capacity.reserved}
                                            timezone={timezone}
                                        />
                                    </div>
                                ) : null}

                                <div className="space-y-2">
                                    <Label className={clsx(errors?.totalGuests && "!text-danger")}>
                                        {errors.totalGuests ?? "Guest Quantity"}
                                    </Label>
                                    <GuestSpinnerList
                                        catalog={getWaitlistCatalogItems(experience)}
                                        experience={experience}
                                        total={totalGuests}
                                        initialQuantity={initialQuantity}
                                        onChange={(guests, catalogItem, quantity) => {
                                            setGuests(guests);
                                            // onQuantityChange syncs demographic spinners on the purchase page; pushing
                                            // equipment templates through it zeroes the item quantity (closes the modal)
                                            if (catalogItem.unitType !== UnitType.EQUIPMENT) {
                                                onQuantityChange?.(catalogItem, quantity);
                                            }

                                            setFieldValue("totalGuests", getTotalGuests(guests));
                                            setTimeout(() => {
                                                validateField("totalGuests");
                                            }, 0);
                                        }}
                                    />
                                </div>

                                <div>
                                    <FormField name="name" label="Name">
                                        <div className="flex space-x-2">
                                            <InputField placeholder="Name" />
                                            {isCustomerSelected ? (
                                                <SecondaryButton
                                                    className="shrink-0"
                                                    onClick={handleRemoveSelectedCustomer(setFieldValue)}
                                                >
                                                    <CloseIcon />
                                                </SecondaryButton>
                                            ) : (
                                                <IsGranted permissions={Permission.SEARCH_CUSTOMERS}>
                                                    <SecondaryButton
                                                        className="shrink-0"
                                                        onClick={showCustomerSearchModal}
                                                    >
                                                        <SearchIcon />
                                                    </SecondaryButton>
                                                </IsGranted>
                                            )}
                                        </div>
                                    </FormField>

                                    <FormField name="email" label="Email">
                                        <InputField placeholder="Email" />
                                    </FormField>

                                    <FormField name="phone" label="Phone">
                                        <InputField placeholder="Phone" />
                                    </FormField>
                                </div>
                                {shouldShowFlexibleAvailability && (
                                    <div className="space-y-4">
                                        <Switch.Group>
                                            <Switch
                                                size="medium"
                                                isChecked={values.timeRange.flexible}
                                                onChange={(value: boolean) => {
                                                    const timeRange = value
                                                        ? { ...values.timeRange, flexible: value }
                                                        : { flexible: value };
                                                    setFieldValue("timeRange", timeRange);
                                                }}
                                            />
                                            <Switch.Label direction="right">
                                                Guests availability is flexible
                                            </Switch.Label>
                                        </Switch.Group>

                                        {values.timeRange.flexible && (
                                            <TimeRangeSelector
                                                showLabel
                                                errors={errors.timeRange}
                                                value={values.timeRange}
                                                startTimeLabel="From"
                                                endTimeLabel="Until"
                                                onChange={(timeRange) => {
                                                    setFieldValue("timeRange", timeRange);
                                                    setTimeout(() => {
                                                        validateField("timeRange");
                                                    }, 0);
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </Modal.Body>

                        <Modal.Footer className="space-x-4">
                            <SecondaryButton data-testid="join-waitlist-modal-cancel-button" onClick={onClose}>
                                Cancel
                            </SecondaryButton>
                            <SubmitButton
                                isLoading={isLoading}
                                isSuccess={isSuccess}
                                data-testid="join-waitlist-modal-join-waitlist-button"
                                onClick={handleSubmit}
                            >
                                Join Waitlist
                            </SubmitButton>
                        </Modal.Footer>
                        <SearchCustomerModal
                            isOpen={isCustomerSearchModal}
                            initialSearch={values.name}
                            onSubmit={(user) => {
                                setFieldValue("name", user.name);
                                setFieldValue("email", user.email ?? "");
                                setFieldValue("phone", user.phone ?? "");

                                setCustomerSelected(true);
                                hideCustomerSearchModal();
                            }}
                            onClose={hideCustomerSearchModal}
                        />
                    </>
                );
            }}
        </XolaFormik>
    );
};

type GuestSpinnerState = { [key: ID]: number };

interface GuestSpinnerListProps {
    catalog: CatalogItem[];
    experience: Experience;
    onChange: (state: GuestSpinnerState, catalog: CatalogItem, quantity: number) => void;
    total: number;
    initialQuantity: DemographicLineItem[];
}

export const GuestSpinnerList = ({ catalog, experience, onChange, total, initialQuantity }: GuestSpinnerListProps) => {
    const [guests, setGuests] = useState<GuestSpinnerState>(getInitialSpinnerState(catalog, initialQuantity));

    const handleChange = (catalog: CatalogItem, quantity: number) => {
        const newState: GuestSpinnerState = { ...guests, [catalog.id!]: quantity };
        setGuests(newState);
        onChange(newState, catalog, quantity);
    };

    return (
        <div className="space-y-2">
            {catalog.map((catalog) => {
                const { id } = catalog;
                const minWaitlistGuest = 1;
                const spare = total - minWaitlistGuest;
                const value = id ? guests[id] ?? 0 : 0;
                const minForDemographic = Math.max(value - spare, 0);
                const max = getWaitlistSpinnerMax(experience, catalog);

                return (
                    <div key={catalog.id} className="flex space-x-4" data-testid="join-waitlist-modal-quantity-spiner">
                        <QuantitySpinner
                            classNames={{ fieldset: "w-24" }}
                            value={value}
                            // QuantitySpinner treats max as a cap on total; equipment caps per row by its own count
                            total={isEquipmentBased(experience) ? 0 : total}
                            min={minForDemographic}
                            max={max}
                            size="large"
                            onChange={(quantity: number) => {
                                handleChange(catalog, quantity);
                            }}
                        />
                        <NoTranslate if={!isDefaultDemographic(catalog.name)} as="div" className="self-start font-bold">
                            {catalog.name}
                        </NoTranslate>
                    </div>
                );
            })}
        </div>
    );
};

const getWaitlistLineItemPayload = (
    timeslot: Timeslot,
    experience: Experience,
    initialLineItems: InitialLineItem[],
    guests: GuestSpinnerState,
    time: Partial<TimeRange>,
    timezone?: string,
) => {
    const payload: any = {
        object: isEquipmentBased(experience) ? PurchaseItemObject.EQUIPMENT : PurchaseItemObject.EXPERIENCE,
        arrivalDate: formatDate(timeslot.arrivalDatetime),
        product: { id: timeslot.product },
        source: Source.OFFICE,
    };

    if (isValidTimeZoneId(timezone)) {
        payload.arrivalTimezoneName = timezone;
    }

    if (time.startTime && time.endTime) {
        payload.arrivalTimeRange = time;
    }

    if (timeslot.time) {
        payload.arrivalTime = timeslot.time;
    }

    const guestLineItemsPayload = getGuestLineItemsPayload(guests, getWaitlistCatalogItems(experience));
    const addonLineItemsPayload = getAddonLineItemsPayload(initialLineItems, experience.catalog.items);

    payload.lineItems = [...guestLineItemsPayload, ...addonLineItemsPayload];

    return payload;
};

const getTotalGuests = (guests: GuestSpinnerState) => {
    return sum(Object.values(guests));
};

const getValidationSchema = (seller: Seller, isCustomerSelected: boolean) => {
    return object().shape({
        name: requiredString(),
        email: isCustomerSelected ? string().email() : requiredEmail(),
        timeRange: object().shape({
            flexible: boolean(),
            startTime: number().when("flexible", {
                is: true,
                then: requiredNumber(),
            }),
            endTime: number()
                .when("flexible", {
                    is: true,
                    then: requiredNumber(),
                })
                .when("startTime", {
                    is: (startTime?: number) => !isNil(startTime),
                    then: number().moreThan(ref("startTime"), "must be after start time"),
                }),
        }),
        totalGuests: number().moreThan(0, "The minimum number of guests needed in order to book is 1"),
        phone: isPurchasePhoneRequired(seller) && !isCustomerSelected ? requiredString() : string(),
    });
};

const getInitialSpinnerState = (
    catalogItems: CatalogItem[],
    initialQuantity: DemographicLineItem[],
): GuestSpinnerState => {
    const guestSpinnerState: GuestSpinnerState = {};

    for (const catalogItem of catalogItems) {
        const { id } = catalogItem;
        const templateId = catalogItem.template?.id ?? null;

        const demographicLineItem = templateId
            ? initialQuantity.find((lineItem) => lineItem.template.id === templateId)
            : null;

        if (demographicLineItem && id) {
            guestSpinnerState[id] = demographicLineItem.quantity;
        }
    }

    return guestSpinnerState;
};

const getInitialValues = (catalogItems: CatalogItem[], initialQuantity: DemographicLineItem[]) => ({
    name: "",
    email: "",
    phone: "",
    flexible: false,
    timeRange: { flexible: false },
    totalGuests: getTotalGuests(getInitialSpinnerState(catalogItems, initialQuantity)),
});

export const getWaitlistCatalogItems = (experience: Experience) =>
    getCatalogItemsByUnitType(
        experience.catalog?.items ?? [],
        isEquipmentBased(experience) ? UnitType.EQUIPMENT : UnitType.DEMOGRAPHIC,
    );

export const getWaitlistSpinnerMax = (experience: Experience, catalogItem: CatalogItem): number => {
    if (isEquipmentBased(experience)) {
        return (catalogItem as EquipmentCatalogItem).count ?? 1;
    }
    return experience?.group?.outingMax ?? Number.MAX_VALUE;
};

const getGuestLineItemsPayload = (guests: GuestSpinnerState, experienceCatalogItems: CatalogItem[]) => {
    const guestLineItemsPayload = [];

    for (const catalogId of Object.keys(guests)) {
        const catalog = experienceCatalogItems.find(({ id }) => id === catalogId);
        if (catalog?.template?.id) {
            const template = { id: catalog.template.id };
            const quantity = guests[catalogId];
            guestLineItemsPayload.push({ template, quantity });
        }
    }

    return guestLineItemsPayload;
};

const getAddonLineItemsPayload = (initialLineItems: InitialLineItem[], experienceCatalogItems: CatalogItem[]) => {
    const addonLineItems = getCreatePurchaseAddonLineItems(initialLineItems as AddonLineItem[]);

    const addonLineItemsPayload = reduce(
        addonLineItems,
        (accumulator, item) => {
            if ("template" in item && "quantity" in item && item.template.id) {
                const { id } = item.template;
                const catalogItem = getCatalogItemByTemplateId(experienceCatalogItems, id);

                if (catalogItem) {
                    return [...accumulator, { template: { id }, quantity: item.quantity }];
                }
            }

            return accumulator;
        },
        [] as Array<{ template: { id: ID }; quantity: number }>,
    );

    return addonLineItemsPayload;
};
